import React, { ReactElement, useState } from 'react';
import {
  ActivityIndicator, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import EllipsisIcon from '../EllipsisIcon';
import AlertModal from '../AlertModal';
import AddTemplateGroupModal from './AddTemplateGroupModal';
import updateTemplateGroup from '../../requests/Assets/UpdateTemplateGroup';
import { logEvent } from '../LogEvent';
import Style from '../../constants/Style';
import { DELETE_TEMPLATE_GROUP_SUCCESS } from '../../constants/AlertMessages';
import { UserPermission } from '../../constants/Types';

export default function TemplateSideBarRow(props) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditTemplate: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const canDeleteTemplate: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_DELETE);
  const [editGroupModalVisible, setEditGroupModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const deleteError = (errorText: string) => showAlert(
    'Error',
    errorText ?? 'Could not delete template group. Please try again later.',
  );

  const deleteTemplateGroup = () => {
    props.contextMenuClose();
    setLoading(true);
    updateTemplateGroup(null, props.group.ButtonPrototypeGroupId, true).then((res) => {
      if (res && !res.isError) {
        props.update(null, props.group.ButtonPrototypeGroupId, false, true);
        props.showScreenAlert(
          DELETE_TEMPLATE_GROUP_SUCCESS,
          <>
            <Text style={Style.alertBoldText}>{props.group.Name}</Text>
            {' was successfully deleted'}
          </>,
          false,
        );
      } else deleteError(res?.errorText);
    }).catch((error) => deleteError(error?.message))
      .finally(() => setLoading(false));
  };

  const selected: boolean = props.selectedGroupID === props.group.ButtonPrototypeGroupId;

  return <>
    <TouchableOpacity
      onPress={props.onPress}
      style={[styles.container, selected && styles.containerSelected]}
    >
      <Text style={[
        styles.sectionTitle,
        props.selectedGroupID === props.group.ButtonPrototypeGroupId
        && styles.sectionTitleSelected,
      ]}>
        {props.templateGroups?.filter((group) => group.ButtonPrototypeGroupId
          === props.group?.ButtonPrototypeGroupId)[0]?.Name}
      </Text>
      {(canEditTemplate || canDeleteTemplate) && <>
        {loading ? <View style={styles.loaderContainer}>
          <ActivityIndicator
            size='small'
            color={selected ? '#FFF' : Colors.light.hereworksBlue400}
          />
        </View> : <TouchableOpacity
          hitSlop={{ left: 40 }}
          style={styles.ellipsis}
          onPress={() => {
            logEvent('TemplateGroupRowWeb', 'Ellipsis');
            if (props.currentContextMenu === props.group.ButtonPrototypeGroupId) {
              props.contextMenuClose();
            } else props.contextMenuOpen();
          }}
          testID='am-template-group-menu-btn'
        >
          <EllipsisIcon
            fill={selected ? '#FFF' : '#D9D9D9'}
            style={{ marginRight: 18 }}
          />
        </TouchableOpacity>}
        {props.currentContextMenu === props.group.ButtonPrototypeGroupId
          && <View style={styles.menu}>
            <TouchableOpacity
              style={styles.contextItem}
              onPress={() => {
                logEvent('TemplateGroupRowWeb', 'Edit_Group');
                props.contextMenuClose();
                setEditGroupModalVisible(!editGroupModalVisible);
              }}
              testID='tmplt-group-menu-edit-btn'
            >
              <Text style={styles.contextItemText}>{'Edit'}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.contextItem}
              onPress={() => {
                logEvent('TemplateGroupRowWeb', 'Delete_Group');
                showAlert(
                  'Delete template group',
                  <>
                    {'Are you sure you want to delete the template group '}
                    <Text style={Style.alertBoldText}>{props.group.Name}</Text>
                  </>,
                  true,
                  deleteTemplateGroup,
                  'Delete',
                );
              }}
              testID='tmplt-group-menu-delete-btn'
            >
              <Text style={styles.contextItemText}>{'Delete'}</Text>
            </TouchableOpacity>
          </View>}
      </>}
    </TouchableOpacity>
    <AddTemplateGroupModal
      templateGroups={props.templateGroups}
      isVisible={editGroupModalVisible}
      hide={() => setEditGroupModalVisible(false)}
      id={props.group.ButtonPrototypeGroupId}
      currentName={props.group.Name}
      update={props.update}
    />
    <AlertModal
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
      {...{ loading }}
      setModalVisible={setAlertVisible}
    />
  </>;
}

const styles = StyleSheet.create({
  container: {
    zIndex: -1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  containerSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  sectionTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    marginLeft: 24,
    marginRight: 50,
    marginVertical: 16,
    color: Colors.light.hereworksBlack500,
  },
  sectionTitleSelected: {
    color: '#FFF',
  },
  contextItem: {
    marginVertical: 9,
    backgroundColor: '#fff',
  },
  contextItemText: {
    marginLeft: 12,
    color: Colors.light.hereworksPrimaryGrey,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    lineHeight: 21,
  },
  ellipsis: {
    position: 'absolute',
    right: 0,
    justifyContent: 'center',
    height: '100%',
  },
  loaderContainer: {
    position: 'absolute',
    right: 0,
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: 56,
  },
  menu: {
    zIndex: 5,
    position: 'absolute',
    top: 0,
    right: 50,
    width: 87,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
    backgroundColor: '#fff',
  },
});
